import i18n from "i18n"

export const PUBLISHING_FIELDS = {
  dsMarketplace: "publish_on_platform",
  frontEndVisibility: "publish_on_frontend_embed"
}

/**
 * Single source of truth for wizard steps.
 *
 * Each step declares:
 *   id      — unique step key, matches STEP_COMPONENTS and URL ?step= param
 *   label   — tab label
 *   title   — page heading (optional)
 *   subTitle — page sub-heading (optional)
 *   percent — progress bar value
 *   fields  — AntD form field names validated on step exit via form.validateFields().
 *             Steps whose data lives outside the form (media, location, etc.) use []
 *             and register custom handlers via stepSaveHandlersRef / stepValidateHandlersRef.
 *
 * Derive getSteps() and STEP_FIELDS from this array — never maintain a separate list.
 */
export const getStepConfig = (listingType) => {
  const isTicketed = listingType === "ticketed"

  return [
    {
      id: "details",
      label: i18n.t("listings.tabs.details.label"),
      title: isTicketed ? i18n.t("listings.tabs.details.title") : i18n.t("listings.tabs.details.private_title"),
      percent: 0,
      fields: [
        "title",
        "description",
        "categories",
        "sub_categories",
        "languages",
        "skill_levels",
        "included_items",
        "bring_items",
        "accessibility_enabled",
        "alcohol_allowed",
        "children_allowed",
        PUBLISHING_FIELDS.frontEndVisibility,
        PUBLISHING_FIELDS.dsMarketplace
      ]
    },
    ...(isTicketed
      ? [
          {
            id: "ticket_type",
            label: i18n.t("listings.tabs.ticket_type.label"),
            title: i18n.t("listings.tabs.ticket_type.title"),
            percent: 15,
            fields: ["duration_type", "number_of_days", "min_tickets", "max_tickets", "ticket_description"]
          }
        ]
      : []),
    {
      id: "scheduling",
      label: isTicketed ? i18n.t("listings.tabs.scheduling.ticketed_label") : i18n.t("listings.tabs.scheduling.label"),
      title: isTicketed ? i18n.t("listings.tabs.scheduling.ticketed_title") : i18n.t("listings.tabs.scheduling.title"),
      percent: 30,
      fields: isTicketed
        ? ["duration_value", "duration_unit", "cut_off_value", "cut_off_unit", "low_availability_threshold"]
        : [
            "duration_type",
            "number_of_days",
            "cut_off_value",
            "cut_off_unit",
            "seasonal",
            "recurringYearly",
            "availability_schedules",
            "schedule_overrides"
          ]
    },
    {
      id: "pricing",
      label: i18n.t("listings.tabs.pricing.label"),
      title: i18n.t("listings.tabs.pricing.title"),
      subTitle: isTicketed ? i18n.t("listings.tabs.pricing.ticketed_subtitle") : undefined,
      percent: 40,
      fields: isTicketed
        ? []
        : ["pricing_type", "min_guests", "max_guests", "licenses_required"]
    },
    {
      id: "resources",
      label: i18n.t("listings.tabs.resources.label"),
      percent: 50,
      fields: []
    },
    // For both ticketed and non-ticketed: location comes before media.
    ...(isTicketed
      ? [
          {
            id: "location",
            label: i18n.t("listings.tabs.location.label"),
            title: i18n.t("listings.tabs.location.title"),
            subTitle: i18n.t("listings.tabs.location.subtitle"),
            percent: 55,
            fields: ["address"]
          }
        ]
      : []),
    // Location (non-ticketed only, since ticketed location is already above)
    ...(isTicketed
      ? []
      : [
          {
            id: "location",
            label: i18n.t("listings.tabs.location.label"),
            title: i18n.t("listings.tabs.location.title"),
            subTitle: i18n.t("listings.tabs.location.subtitle"),
            percent: 55,
            fields: ["address"]
          }
        ]),
    {
      id: "media",
      label: i18n.t("listings.tabs.media.label"),
      title: i18n.t("listings.tabs.media.title"),
      subTitle: i18n.t("listings.tabs.media.subtitle"),
      percent: 65,
      fields: []
    },
    {
      id: "additional_info",
      label: i18n.t("listings.tabs.additional_info.label"),
      title: i18n.t("listings.tabs.additional_info.title"),
      subTitle: i18n.t("listings.tabs.additional_info.subtitle"),
      percent: 88,
      fields: []
    },
    {
      id: "preview",
      label: i18n.t("listings.tabs.preview.label"),
      title: i18n.t("listings.tabs.preview.title"),
      subTitle: i18n.t("listings.tabs.preview.subtitle"),
      percent: 95,
      fields: []
    }
  ]
}

/**
 * Ordered list of step IDs — derived from getStepConfig so there is no second list to maintain.
 */
export const getSteps = (listingType) => getStepConfig(listingType).map((s) => s.id)

/**
 * Map of step ID → form field names — derived from getStepConfig.
 * Used by useExperienceWizard to run form.validateFields() on step exit.
 */
export const getStepFields = (listingType) =>
  Object.fromEntries(getStepConfig(listingType).map((s) => [s.id, s.fields]))

// The fields a new listing needs before Save Draft and Exit creates it half-filled — its name, so the
// draft can be found in Drafts.
export const NEW_DRAFT_REQUIRED_FIELDS = ["title"]

// Left out of a half-filled draft save, as the server would refuse it otherwise. wizard_step tells it
// every step up to this one is complete, so it checks them all; an empty sub-category list it refuses
// outright. Publishing still checks everything.
export const PARTIAL_DRAFT_LEFT_OUT_FIELDS = {
  wizardStep: "trip[wizard_step]",
  emptySubcategories: "trip[subcategory_ids][]"
}

/**
 * The server's wizard_step (the last tab a guide saved, as each step's payload names it) → the
 * wizard's tab id. Details saves don't set it, so it has no entry.
 */
export const SAVED_WIZARD_STEP_TO_TAB = {
  ticket_type: "ticket_type",
  schedule: "scheduling",
  pricing: "pricing",
  boats: "resources",
  location: "location",
  media: "media",
  additional_info: "additional_info",
  preview: "preview"
}

/**
 * When the server refuses to publish (the Preview save runs every step's checks), it names each
 * missing field. Each maps to the step that sets it and a plain line for the guide
 * (experiences.publish_check.<messageKey>). Fields not listed here get the "other" line.
 */
export const PUBLISH_REFUSAL_FIELDS = {
  latitude: { stepId: "location", messageKey: "meeting_point" },
  longitude: { stepId: "location", messageKey: "meeting_point" },
  duration_value: { stepId: "scheduling", messageKey: "duration" },
  duration_unit: { stepId: "scheduling", messageKey: "duration" },
  experience_time_slots: { stepId: "scheduling", messageKey: "start_times" },
  trip_availability_schedules: { stepId: "scheduling", messageKey: "availability_schedule" },
  ticket_types: { stepId: "pricing", messageKey: "ticket_prices" },
  trip_prices: { stepId: "pricing", messageKey: "booking_options" }
}

export const PUBLISH_REFUSAL_OTHER = { stepId: null, messageKey: "other" }
